import { useEffect, useRef } from 'react';

import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

const DRAG_THRESHOLD = 5;

type Props = {
  onObjectClick: (name: string) => void;
};

/**
 * R3F component that handles right-click (button 2) on 3D meshes using native
 * DOM pointer events + manual raycasting. This bypasses R3F's event system,
 * which doesn't reliably fire onContextMenu when OrbitControls is active.
 */
export function RightClickHandler({ onObjectClick }: Props) {
  const { gl, camera, raycaster, scene } = useThree();
  const downPos = useRef<{ x: number; y: number } | null>(null);
  const travel = useRef(0);
  const onObjectClickRef = useRef(onObjectClick);
  onObjectClickRef.current = onObjectClick;

  useEffect(() => {
    const canvas = gl.domElement;

    const onDown = (e: PointerEvent) => {
      if (e.button === 2) {
        downPos.current = { x: e.clientX, y: e.clientY };
        travel.current = 0;
      }
    };

    const onMove = (e: PointerEvent) => {
      if (!downPos.current) return;
      const dx = e.clientX - downPos.current.x;
      const dy = e.clientY - downPos.current.y;
      travel.current = Math.sqrt(dx * dx + dy * dy);
    };

    const onUp = (e: PointerEvent) => {
      if (e.button !== 2) return;
      const wasDrag = travel.current > DRAG_THRESHOLD;
      downPos.current = null;
      travel.current = 0;
      if (wasDrag) return;

      // Raycast to find the clicked mesh
      const rect = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(scene.children, true);
      const hit = hits.find((h) => h.object.type === 'Mesh' && h.object.name);
      if (hit) {
        onObjectClickRef.current(hit.object.name);
      }
    };

    const onCtx = (e: Event) => {
      e.preventDefault();
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('contextmenu', onCtx);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('contextmenu', onCtx);
    };
  }, [gl, camera, raycaster, scene]);

  return null;
}
